<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import api from "../../api/axios";
import DataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";

import ArgonButton from "@/components/ArgonButton.vue";
import ArgonInput from "@/components/ArgonInput.vue";

const router = useRouter();

const companies = ref([]);
const cargando = ref(true);
const search = ref("");

const showModal = ref(false);
const isEdit = ref(false);
const guardando = ref(false);

const form = ref({
  id: null,
  name: "",
  nit: "",
  industry: "",
  city: "",
  country: "",
  website: "",
  employeeCount: "",
  revenue: "",
  notes: ""
});

const headers = [
  { text: "Empresa", value: "name" },
  { text: "Industria", value: "industry" },
  { text: "Ciudad", value: "city" },
  { text: "País", value: "country" },
  { text: "Contactos", value: "contact_count" },
  { text: "Negocios", value: "deal_count" },
  { text: "Acciones", value: "actions", sortable: false }
];

const getCompanies = async () => {
  const res = await api.get("/crm/companies", { params: { search: search.value, limit: 200 } });
  companies.value = res.data.companies;
};

const resetForm = () => {
  form.value = { id: null, name: "", nit: "", industry: "", city: "", country: "", website: "", employeeCount: "", revenue: "", notes: "" };
};

const abrirCrear = () => {
  resetForm();
  isEdit.value = false;
  showModal.value = true;
};

const abrirEditar = (company) => {
  form.value = {
    id: company.id,
    name: company.name,
    nit: company.nit || "",
    industry: company.industry || "",
    city: company.city || "",
    country: company.country || "",
    website: company.website || "",
    employeeCount: company.employee_count || "",
    revenue: company.revenue || "",
    notes: company.notes || ""
  };
  isEdit.value = true;
  showModal.value = true;
};

const guardar = async () => {
  if (!form.value.name.trim()) {
    Swal.fire({ icon: "warning", title: "El nombre es obligatorio" });
    return;
  }

  guardando.value = true;

  try {
    if (isEdit.value) {
      await api.put(`/crm/companies/${form.value.id}`, form.value);
    } else {
      await api.post("/crm/companies", form.value);
    }

    showModal.value = false;
    await getCompanies();

    Swal.fire({
      icon: "success",
      title: isEdit.value ? "Empresa actualizada" : "Empresa creada",
      timer: 1800,
      showConfirmButton: false
    });

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo guardar la empresa",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardando.value = false;
  }
};

const eliminar = async (company) => {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: `¿Eliminar la empresa "${company.name}"?`,
    text: "Los contactos y negocios vinculados no se borran, solo quedan sin empresa asociada.",
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  });

  if (!isConfirmed) return;

  await api.delete(`/crm/companies/${company.id}`);
  await getCompanies();
};

const verDetalle = (company) => {
  router.push(`/crm/companies/${company.id}`);
};

onMounted(async () => {
  cargando.value = true;
  try {
    await getCompanies();
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudieron cargar las empresas",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    cargando.value = false;
  }
});
</script>

<template>
  <div class="container-fluid py-4">

    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="mb-0 companies-header-title">
        <i class="bi bi-building me-2"></i>
        Empresas
      </h4>

      <argon-button color="success" @click="abrirCrear">
        <i class="bi bi-plus-circle me-1"></i>Nueva empresa
      </argon-button>
    </div>

    <div class="mb-3" style="max-width: 320px">
      <argon-input v-model="search" placeholder="Buscar por nombre..." @keyup.enter="getCompanies" />
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <div v-else class="card">
      <div class="card-body">
        <DataTable :headers="headers" :items="companies" :rows-per-page="20">
          <template #item-name="item">
            <span class="text-primary" style="cursor: pointer" @click="verDetalle(item)">{{ item.name }}</span>
          </template>

          <template #item-actions="item">
            <div class="d-flex gap-1">
              <button class="btn btn-sm btn-light" @click="abrirEditar(item)">
                <i class="bi bi-pencil"></i>
              </button>
              <button class="btn btn-sm btn-outline-danger" @click="eliminar(item)">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </template>
        </DataTable>
      </div>
    </div>

    <!-- MODAL CREAR/EDITAR EMPRESA -->
    <div v-if="showModal" class="modal fade show d-block">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">

          <div class="modal-header">
            <h5>{{ isEdit ? "Editar empresa" : "Nueva empresa" }}</h5>
            <button class="btn-close" @click="showModal = false"></button>
          </div>

          <div class="modal-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label small">Nombre</label>
                <argon-input v-model="form.name" placeholder="Nike" />
              </div>
              <div class="col-md-6">
                <label class="form-label small">NIT</label>
                <argon-input v-model="form.nit" />
              </div>

              <div class="col-md-6">
                <label class="form-label small">Industria</label>
                <argon-input v-model="form.industry" placeholder="Retail deportivo" />
              </div>
              <div class="col-md-6">
                <label class="form-label small">Sitio web</label>
                <argon-input v-model="form.website" placeholder="https://..." />
              </div>

              <div class="col-md-6">
                <label class="form-label small">Ciudad</label>
                <argon-input v-model="form.city" />
              </div>
              <div class="col-md-6">
                <label class="form-label small">País</label>
                <argon-input v-model="form.country" />
              </div>

              <div class="col-md-6">
                <label class="form-label small">Cantidad de empleados</label>
                <argon-input v-model="form.employeeCount" type="number" />
              </div>
              <div class="col-md-6">
                <label class="form-label small">Ingresos (COP)</label>
                <argon-input v-model="form.revenue" type="number" />
              </div>

              <div class="col-12">
                <label class="form-label small">Observaciones</label>
                <textarea v-model="form.notes" class="form-control" rows="3"></textarea>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary mb-0" @click="showModal = false">Cancelar</button>
            <argon-button color="success" :disabled="guardando" @click="guardar">Guardar</argon-button>
          </div>

        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-backdrop fade show"></div>

  </div>
</template>

<style scoped>
.companies-header-title {
  color: #fff !important;
}
</style>
